"use client";

import { useMemo, type RefObject } from "react";
import { ChevronDown, X } from "lucide-react";
import { useDismissPopoverOnScroll } from "./useDismissPopoverOnScroll";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import {
  filterCheckboxClass,
  filterChipActiveClass,
  filterChipInactiveClass,
} from "./filterStyles";
import type { FilterOption } from "@/types/api/packages";

interface FilterDropdownProps {
  label: string;
  options: FilterOption[];
  selectedIds: number[];
  onToggle: (optionId: number) => void;
  onClear?: () => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scrollContainerRef?: RefObject<HTMLElement | null>;
  className?: string;
}

export function FilterDropdown({
  label,
  options,
  selectedIds,
  onToggle,
  onClear,
  open,
  onOpenChange,
  scrollContainerRef,
  className,
}: FilterDropdownProps) {
  const isActive = selectedIds.length > 0;

  useDismissPopoverOnScroll(open, () => onOpenChange(false), scrollContainerRef);

  const triggerLabel = useMemo(() => {
    if (selectedIds.length === 0) return label;
    if (selectedIds.length === 1) {
      const option = options.find((o) => o.id === selectedIds[0]);
      return option?.label ?? label;
    }
    return `${label} (${selectedIds.length})`;
  }, [label, options, selectedIds]);

  return (
    <Popover modal open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            isActive || open ? filterChipActiveClass : filterChipInactiveClass,
            "gap-1.5",
            className
          )}>
          <span className="whitespace-nowrap">{triggerLabel}</span>
          {isActive && onClear ? (
            <span
              role="button"
              tabIndex={0}
              className="inline-flex shrink-0"
              aria-label={`Clear ${label}`}
              onClick={(e) => {
                e.stopPropagation();
                onClear();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  onClear();
                }
              }}>
              <X className="h-3.5 w-3.5 shrink-0 opacity-80" strokeWidth={2.5} />
            </span>
          ) : (
            <ChevronDown
              className={cn(
                "h-4 w-4 shrink-0 transition-transform",
                open && "rotate-180"
              )}
            />
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={8}
        collisionPadding={16}
        className="z-60 w-64 rounded-2xl border border-gray-blue-200 bg-white p-3 shadow-xl"
        onOpenAutoFocus={(e) => e.preventDefault()}>
        <p className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
          {label}
        </p>
        <div className="max-h-64 space-y-0.5 overflow-y-auto">
          {options.map((option) => {
            const isSelected = selectedIds.includes(option.id);

            return (
              <div
                key={option.id}
                role="button"
                tabIndex={0}
                className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2 hover:bg-gray-blue-50"
                onClick={() => onToggle(option.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onToggle(option.id);
                  }
                }}>
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => onToggle(option.id)}
                  className={filterCheckboxClass}
                  onClick={(e) => e.stopPropagation()}
                />
                <Label className="cursor-pointer text-sm font-medium text-gray-900">
                  {option.label}
                </Label>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
